import React, { useState, useEffect } from 'react';
import { Gift, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const PromotionSettings = () => {
  const [settings, setSettings] = useState({ enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/coupon-promo`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/coupon-promo`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8">Lade Einstellungen...</div>;
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-8">Promotion-Einstellungen</h1>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Coupon-Promotion auf Homepage
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-base font-medium">Coupon-Promotion Abschnitt anzeigen</Label>
              <p className="text-sm text-gray-600 mt-1">
                Zeigt den Willkommens-Coupon Abschnitt auf der Homepage an
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {settings.enabled ? 'Aktiviert' : 'Deaktiviert'}
              </span>
              <button
                onClick={() => setSettings({...settings, enabled: !settings.enabled})}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.enabled ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>💡 Info:</strong> Wenn deaktiviert, wird der Coupon-Promotion-Abschnitt
              nicht auf der Homepage angezeigt. Das Coupon-System selbst bleibt aber aktiv.
            </p>
          </div>

          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Speichert...' : 'Einstellungen speichern'}
          </Button>
        </CardContent>
      </Card>

      <Card className="mt-6" data-testid="promo-banner-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Gutschein-Banner oben & Willkommens-Gutschein
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.banner_enabled !== false}
              onChange={(e) => setSettings({ ...settings, banner_enabled: e.target.checked })}
              data-testid="promo-banner-enabled"
            />
            Banner oben im Shop anzeigen
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Banner-Text (z. B. „-10 %“)</Label>
              <input className="w-full border px-3 py-2 text-sm mt-1" value={settings.banner_text || ''} onChange={(e) => setSettings({ ...settings, banner_text: e.target.value })} data-testid="promo-banner-text" />
            </div>
            <div>
              <Label>Gutscheincode im Banner</Label>
              <input className="w-full border px-3 py-2 text-sm mt-1 uppercase" value={settings.banner_code || ''} onChange={(e) => setSettings({ ...settings, banner_code: e.target.value })} data-testid="promo-banner-code" />
            </div>
            <div className="sm:col-span-2">
              <Label>Code in der Newsletter-Willkommens-E-Mail</Label>
              <input className="w-full border px-3 py-2 text-sm mt-1 uppercase" value={settings.newsletter_code || ''} onChange={(e) => setSettings({ ...settings, newsletter_code: e.target.value })} data-testid="promo-newsletter-code" />
              <p className="text-xs text-gray-500 mt-1">Der Code muss unter „Rabattcodes“ angelegt und gültig sein.</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="promo-banner-save">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Speichert...' : 'Banner speichern'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PromotionSettings;
